import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, take, tap } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { isEqual } from 'lodash';
import { AccessControl } from '@pncl/common-models';
import { CapabilityTierService } from 'src/app/services/capability-tier.service';
import { AttendanceUserRole } from 'src/app/sessions/session/attendance/attendance-ui-models';
import { PanelData } from '../../settings.constants';
import { AttendanceStatus, AttendanceThresholdRule, User } from '../../../models/user';
import { UserService } from '../../../services/user.service';
import { SettingsService } from '../../../services/settings.service';
import { SettingsNotificationService } from '../../shared/services/settings-notifications.service';
import Capability = AccessControl.Capability;

@UntilDestroy()
@Component({
  selector: 'app-user-settings-events',
  templateUrl: './user-settings-events.component.html',
  styleUrls: ['./user-settings-events.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsEventsComponent implements OnInit {
  @Input() form!: UntypedFormGroup;
  @Input() isMobile = false;
  @Input() panelData?: PanelData;
  @Output() goBack = new EventEmitter<any>();
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();

  settingsForm?: UntypedFormGroup;
  user?: User;
  isLoading = false;
  public initialFormValue: Record<string, unknown> | null = null;
  protected AttendanceStatus = AttendanceStatus;
  protected AttendanceUserRole = AttendanceUserRole;
  public formChanged$ = new BehaviorSubject<boolean>(false);
  isStartCallEnabled$ = this.capabilityTierService.shouldEnableCapability$(Capability.START_CALL);
  readonly attendanceMinuteOptions = Array.from({ length: 6 }, (_, i) => ({
    minutes: (i + 1) * 5,
    label: this.translateService.instant('{{minutes}} minutes', { minutes: (i + 1) * 5 }),
  }));
  readonly absentMinuteOptions = [
    { minutes: null, label: this.translateService.instant('Event is over') },
    ...this.attendanceMinuteOptions,
  ];

  constructor(
    private userService: UserService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private settingsNotificationService: SettingsNotificationService,
    private capabilityTierService: CapabilityTierService,
  ) {}

  ngOnInit(): void {
    this.settingsForm = this.form?.controls.settings as UntypedFormGroup;
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.initForm();
    });
  }

  initForm(): void {
    this.form.patchValue(
      {
        ...this.user?.institution,
        settings: {
          ...(this.user?.institution?.settings || {}),
        },
      },
      // Do not emit an event when the form is initialized.
      { emitEvent: false },
    );

    this.initialFormValue = this.form.value;

    this.form.valueChanges
      .pipe(
        map(() => !isEqual(this.initialFormValue, this.form.value)),
        tap((x) => this.formChangedEvent.emit(x)),
        untilDestroyed(this),
      )
      .subscribe(this.formChanged$);
  }

  submitChanges(): void {
    if (!this.user || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const updatedUser = {
      ...this.user,
      institution: {
        ...this.user?.institution,
        settings: {
          ...this.user?.institution.settings,
          ...this.settingsForm?.value,
        },
      },
    } as User;

    this.userService
      .updateInstitution(updatedUser)
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.isLoading = false;
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
        },
        complete: () => {
          this.isLoading = false;
          this.formChanged$.next(false);
          this.formChangedEvent.emit(false);
          this.initialFormValue = this.form.value;
          const userFromService = this.userService.user.getValue();
          this.userService.user.next({
            ...userFromService,
            user: updatedUser,
          });
          this.settingsNotificationService.showSuccessSettingsUpdateNotification();
        },
      });
  }

  cancelChanges(): void {
    if (!this.initialFormValue) {
      return;
    }

    this.form.patchValue(this.initialFormValue, { emitEvent: false });
    this.formChanged$.next(false);
    this.formChangedEvent.emit(false);
  }

  /**
   * Adds, updates, or clears a single threshold rule in the `attendanceThresholds` form control.
   *
   * The form stores thresholds as a flat array of `{ status, role, minutes }` rules — one per
   * `status × role` cell — because that is the shape the backend expects on submit.
   * Since there is no dedicated control per cell, changes are applied by rewriting the full array.
   *
   * Passing `null` for `minutes` removes the rule entirely (the "None" option).
   * Re-selecting the already-saved value is a no-op to avoid marking the form dirty with no real change.
   */
  setThreshold(status: AttendanceStatus, role: AttendanceUserRole, minutes: number | null): void {
    const thresholdsControl = this.settingsForm?.get('attendanceThresholds');
    if (!thresholdsControl) {
      return;
    }

    const currentThresholdRules = (thresholdsControl.value as AttendanceThresholdRule[]) ?? [];
    const currentMinutes =
      currentThresholdRules.find((rule) => rule.status === status && rule.role === role)?.minutes ??
      null;

    // Avoid rewriting the array when the user re-selects the already-saved value.
    // Without this guard, filter+append shifts the entry to the end of the array,
    // causing isEqual to report a false change and incorrectly showing the unsaved-changes footer.
    if (currentMinutes === minutes) {
      return;
    }

    // Always remove the existing rule first so add, update, and clear share one path.
    const remainingThresholdRules = currentThresholdRules.filter(
      (rule) => rule.status !== status || rule.role !== role,
    );

    thresholdsControl.setValue(
      minutes === null
        ? remainingThresholdRules
        : [...remainingThresholdRules, { status, role, minutes }],
    );
  }
}
